import { z } from 'zod'

// ---------- Business profile (SPEC.md 4.1) ----------

export const INDUSTRIES = [
  'Dental practice',
  'Medical practice',
  'Physiotherapy',
  'Hair and beauty salon',
  'Fitness studio or gym',
  'Construction',
  'Roofing',
  'Craft trade',
  'Real estate',
  'Consulting',
  'Restaurant or cafe',
  'Retail',
  'Other',
] as const

export const SIZES = ['1 to 10 employees', '11 to 50 employees', '51 to 200 employees', 'More than 200 employees'] as const

export const PriceItem = z.object({
  service: z.string().min(1).max(200),
  price: z.string().min(1).max(100),
})

export const LinkItem = z.object({
  label: z.string().min(1).max(100),
  url: z.string().url().max(500),
})

/** Every value the owner can confirm. Empty string / empty list means "not set". */
export const ProfileData = z.object({
  businessName: z.string().max(200),
  website: z.string().max(500),
  industry: z.string().max(100),
  size: z.string().max(100),
  locationsAndHours: z.string().max(1000),
  offer: z.string().max(3000),
  whyChooseUs: z.string().max(2000),
  typicalQuestions: z.string().max(2000),
  prices: z.array(PriceItem).max(50),
  links: z.array(LinkItem).max(20),
  languages: z.array(z.string().min(1).max(50)).max(10),
  handover: z.string().max(1000),
})
export type ProfileData = z.infer<typeof ProfileData>
export type ProfileField = keyof ProfileData

export const PROFILE_FIELDS = Object.keys(ProfileData.shape) as ProfileField[]

/** Fields that must be confirmed before payment. `prices` and `links` may be confirmed empty. */
export const REQUIRED_PROFILE_FIELDS: ProfileField[] = [
  'businessName',
  'website',
  'industry',
  'locationsAndHours',
  'offer',
  'languages',
  'handover',
]

export const FieldState = z.enum(['empty', 'ai_draft', 'confirmed'])
export type FieldState = z.infer<typeof FieldState>

export const Profile = z.object({
  version: z.number().int().nonnegative(),
  data: ProfileData,
  fieldStates: z.record(z.string(), FieldState),
  /** Provenance of AI drafts: website URLs each field was drafted from. Empty for owner-typed values. */
  fieldSources: z.record(z.string(), z.array(z.string().max(500)).max(10)).default({}),
})
export type Profile = z.infer<typeof Profile>

export const emptyProfile = (): ProfileData => ({
  businessName: '',
  website: '',
  industry: '',
  size: '',
  locationsAndHours: '',
  offer: '',
  whyChooseUs: '',
  typicalQuestions: '',
  prices: [],
  links: [],
  languages: [],
  handover: '',
})

// ---------- Calibration (SPEC.md 4.3) ----------

export const CALIBRATION_TOPICS = [
  'tone',
  'length',
  'prices',
  'handover',
  'booking_push',
  'complaints',
  'after_hours',
  'urgent',
  'unknown_question',
  'opening',
] as const
export const CalibrationTopic = z.enum(CALIBRATION_TOPICS)

export const CalibrationQuestion = z.object({
  id: z.string().min(1).max(40),
  topic: CalibrationTopic,
  contactMessage: z.string().min(1).max(1000),
  candidates: z.array(z.string().min(1).max(1000)).min(2).max(3),
  followUp: z.string().max(300).optional(),
})
export type CalibrationQuestion = z.infer<typeof CalibrationQuestion>

export const CalibrationAnswer = z.object({
  questionId: z.string().min(1).max(40),
  /** Index of the picked candidate, or null when the owner wrote their own reply. */
  picked: z.number().int().min(0).max(2).nullable(),
  /** The final reply as the owner wants it (the pick, possibly edited). */
  reply: z.string().min(1).max(1000),
  followUpAnswer: z.string().max(300).optional(),
})
export type CalibrationAnswer = z.infer<typeof CalibrationAnswer>

// ---------- Preference data and rule lineage (SPEC.md 4.3, 4.4) ----------
// Preference collection in the RLHF sense; no model is trained. Every rule the assistant follows
// points to the preference records that justify it, so each instruction can be traced and audited.

export const PreferenceSource = z.enum(['calibration', 'takeover', 'rating'])
export type PreferenceSource = z.infer<typeof PreferenceSource>

export const PreferenceRecord = z.object({
  id: z.string().min(1).max(40),
  source: PreferenceSource,
  /** What the contact wrote (personal data removed for takeover/rating records). */
  contactMessage: z.string().max(1500),
  /** The reply the owner wants. */
  chosen: z.string().max(1500),
  /** Replies the owner did not pick, or the assistant's draft the owner replaced. */
  rejected: z.array(z.string().max(1500)).max(3),
  /** true when the owner edited a candidate or wrote their own. */
  edited: z.boolean(),
  /** Calibration topic or other short label. */
  label: z.string().max(60).optional(),
  createdAt: z.number().int(),
})
export type PreferenceRecord = z.infer<typeof PreferenceRecord>

export const RuleOrigin = z.enum(['calibration', 'takeover', 'owner'])
export type RuleOrigin = z.infer<typeof RuleOrigin>

export const Rule = z.object({
  id: z.string().min(1).max(40),
  text: z.string().min(1).max(300),
  origin: RuleOrigin,
  /** IDs of PreferenceRecords behind the rule. Required (non-empty) unless origin is 'owner'. */
  evidence: z.array(z.string().max(40)).max(20),
  /** Prompt version and model of the Strands agent that drafted it; null for owner-written rules. */
  draftedBy: z.object({ agent: z.string().max(60), promptVersion: z.string().max(40), modelId: z.string().max(80) }).nullable(),
  createdAt: z.number().int(),
  confirmedAt: z.number().int().nullable(),
})
export type Rule = z.infer<typeof Rule>

/** A rule is admissible only with evidence, or when the owner wrote it. Enforced in code, not by the model. */
export const ruleHasLineage = (r: Pick<Rule, 'origin' | 'evidence'>) => r.origin === 'owner' || r.evidence.length > 0

export const Calibration = z.object({
  version: z.number().int().nonnegative(),
  questions: z.array(CalibrationQuestion),
  answers: z.array(CalibrationAnswer),
  /** Preference records created from the answers (one per answered question). */
  preferences: z.array(PreferenceRecord),
  rules: z.array(Rule).max(40),
  rulesConfirmed: z.boolean(),
})
export type Calibration = z.infer<typeof Calibration>

// ---------- Past situations (SPEC.md 4.2) ----------

export const Situation = z.object({
  id: z.string(),
  situation: z.string().min(1).max(1000),
  whatWeDid: z.string().min(1).max(1000),
  avoid: z.string().max(500).default(''),
  outcome: z.enum(['booked', 'lost', 'complained', 'unknown']).default('unknown'),
})
export type Situation = z.infer<typeof Situation>

// ---------- Lead follow-up settings (SPEC.md 5.1) ----------

export const LeadFollowUpSettings = z.object({
  enabled: z.boolean().default(true),
  bookingLink: z.string().url().max(500).or(z.literal('')),
  qualifyingQuestions: z.array(z.string().min(1).max(200)).max(5),
  /** Header prefixed to every assistant reply, e.g. "Praxis Weber AI". */
  replyHeader: z.string().max(60),
  /** Owner's own WhatsApp number (E.164 digits, no +): their messages are tests, never capped or counted. Alerts go by email. */
  ownerPhone: z.string().regex(/^\d{8,15}$/).or(z.literal('')),
})
export type LeadFollowUpSettings = z.infer<typeof LeadFollowUpSettings>

// ---------- Limits ----------

export const Limits = z.object({
  maxRepliesPerConversation: z.number().int().min(1).max(20).default(5),
  maxNewConversationsPerDay: z.number().int().min(1).max(1000).default(100),
  maxInboundChars: z.number().int().min(100).max(5000).default(1500),
  monthlyBudgetEur: z.number().min(0).max(1000).default(10),
  takeoverHours: z.number().min(0.25).max(72).default(4),
  /** Leads (counted conversations, all channels) per calendar month included in the plan. */
  maxLeadsPerMonth: z.number().int().min(1).max(100_000).default(2000),
})
export type Limits = z.infer<typeof Limits>
export const DEFAULT_LIMITS: Limits = Limits.parse({})

// ---------- Compiled config pushed to the Tenant (SPEC.md 8.4) ----------

export const Example = z.object({
  contact: z.string().max(1000),
  reply: z.string().max(1000),
  note: z.string().max(500).optional(),
})
export type Example = z.infer<typeof Example>

export const CompiledConfig = z.object({
  version: z.number().int().positive(),
  clientId: z.string(),
  businessName: z.string(),
  /** Only confirmed profile values. */
  profile: ProfileData.partial(),
  /** Confirmed rules only; IDs are cited in the Tenant's audit log. */
  rules: z.array(z.object({ id: z.string(), text: z.string() })),
  examples: z.array(Example),
  leadFollowUp: LeadFollowUpSettings,
  limits: Limits,
  /** false = paused (billing, operator, or not yet live). Inbound is stored, nothing is sent. */
  assistantLive: z.boolean(),
  /** Testing (before go-live): the assistant answers only the owner's own contacts; everyone else is stored only. */
  ownerOnly: z.boolean().optional(),
  /** IANA time zone for day and month boundaries and the nightly retention run (default Europe/Berlin). */
  timezone: z.string().min(1).max(64).optional(),
})
export type CompiledConfig = z.infer<typeof CompiledConfig>

// ---------- Client status ----------

export const CLIENT_STATUSES = [
  'onboarding',
  'payment_pending',
  'provisioning',
  'pairing',
  'testing',
  'live',
  'paused',
  'failed',
  'cancelled',
] as const
export const ClientStatus = z.enum(CLIENT_STATUSES)
export type ClientStatus = z.infer<typeof ClientStatus>

// ---------- Channels ----------
// Meta channels (docs/superpowers/specs/2026-10-20-meta-platform-cloudflare-design.md 3.2): WhatsApp (Cloud API,
// Coexistence), Messenger (Facebook Page, incl. Page comments) and Instagram (DMs and comments). Webhooks land on the
// control Worker (apps/control/src/meta), are normalised to `InboundEvent` and handed to the client's Tenant Durable
// Object. A client needs at least one connected channel to go live.

export const CHANNEL_KINDS = ['whatsapp', 'messenger', 'instagram'] as const
export type ChannelKind = (typeof CHANNEL_KINDS)[number]
export const ChannelKindSchema = z.enum(CHANNEL_KINDS)
/** Where an inbound item came from inside a channel. */
export type InboundSource = 'dm' | 'comment'

/** Normalised Meta event, produced by apps/control/src/meta, consumed by Tenant.ingest. */
export type InboundEvent =
  | {
      type: 'message'
      channel: ChannelKind
      source: InboundSource            // 'comment' only for messenger (FB Page) and instagram
      externalId: string               // phone_number_id | page id | instagram account id (routing key)
      contactId: string                // wa_id | PSID | IGSID | commenter id
      contactName?: string             // WhatsApp profile name, comment author name
      messageId: string                // wamid | mid | comment id  (dedupe key)
      text: string                     // media → placeholder like "[image]"
      at: number                       // ms
      fromMe: boolean                  // owner/staff typed (WA smb_message_echoes, FB/IG echo with foreign app_id, page-authored comment)
      standby?: boolean                // Messenger handover protocol
      comment?: { postId: string; parentId?: string; permalink?: string }
      replyTo?: string                 // quoted message id / button payload
    }
  | { type: 'thread_owner'; channel: 'messenger' | 'instagram'; externalId: string; contactId: string; owner: 'us' | 'inbox'; at: number }
  | { type: 'status'; channel: 'whatsapp'; externalId: string; messageId: string; status: 'sent' | 'delivered' | 'read' | 'failed'; errorCode?: number; at: number }
  | { type: 'history'; channel: 'whatsapp'; externalId: string; items: Array<{ contactId: string; messageId: string; text: string; at: number; fromMe: boolean }> }
  | { type: 'account'; channel: ChannelKind; externalId: string; event: 'disconnected' | 'token_invalid' | 'payment_issue' | 'restricted' | 'reinstated'; detail?: string; at: number }

/** Contact key inside a tenant: `${channel}:${contactId}` (e.g. `whatsapp:4915112345678`, `messenger:6789`). */
export const contactKey = (channel: ChannelKind, contactId: string) => `${channel}:${contactId}`

export type ChannelState = 'not_connected' | 'connected' | 'token_invalid' | 'needs_billing' | 'payment_issue' | 'disconnected'

export interface ChannelStatus {
  kind: ChannelKind
  state: ChannelState
  externalId: string | null
  displayName: string | null          // phone number, Page name, @username
  comments?: boolean                  // messenger/instagram: comment replies on
  billingConfirmedAt?: number | null  // whatsapp
}

// ---------- Tenant runtime (Durable Object `Tenant`, apps/control/src/tenant) ----------

export type TenantChannelState = 'connected' | 'token_invalid' | 'payment_issue' | 'disconnected' | 'restricted'

/** A channel as the Tenant knows it: set by control (`set_channel`), changed by account events and send errors. */
export interface TenantChannel {
  kind: ChannelKind
  externalId: string
  displayName: string
  state: TenantChannelState
  /** Messenger/Instagram: answer comments (private reply, optional short public reply). */
  comments: boolean
}

/** `Tenant.command` (control → Tenant). Tokens never reach the Tenant; sends load them from D1 per send. */
export type TenantCommand =
  | { type: 'pause' }
  | { type: 'resume' }
  | {
      type: 'set_channel'
      channel: ChannelKind
      externalId: string
      displayName: string
      /** Messenger/Instagram comment replies (default on). */
      comments?: boolean
      /** Owner test code: the first DM "LOBBY <code>" marks the sender as the owner's own account (never counted). */
      ownerCode?: string
    }
  | { type: 'remove_channel'; channel: ChannelKind }
  /**
   * Control changed the channel's D1 state (daily token check, reconnect, billing). The Tenant mirrors it, alerts the
   * owner (notice + email) for a problem state and sends a `channel` live frame with `status` (the D1 view).
   */
  | { type: 'channel_state'; channel: ChannelKind; state: TenantChannelState; detail?: string; status: ChannelStatus }
  | { type: 'takeover'; conversationId: string }
  | { type: 'handback'; conversationId: string }

/** `Tenant.status()`: numbers only, never message content. */
export interface TenantStatus {
  clientId: string
  configVersion: number
  assistantLive: boolean
  paused: boolean
  channels: TenantChannel[]
  lastMessageAt: number | null
  medianFirstReplyMsToday: number | null
  conversationsThisMonth: number
  /** Conversations from the owner's own number or account (tests); never counted toward limits. */
  testConversationsThisMonth: number
  inputTokensThisMonth: number
  outputTokensThisMonth: number
  spendThisMonthEur: number
  budgetEur: number
  needsYou: number
  /** Open dashboard notices (owner alerts not dismissed yet). */
  notices: number
}

/** Dashboard banner stored by the Tenant for every owner alert (GET /api/clients/:id/d/notices). */
export interface TenantNotice {
  id: string
  kind: OwnerNotice['kind']
  /** Fixed text per kind (never contact data). */
  text: string
  conversationId: string | null
  at: number
  dismissedAt: number | null
}

// ---------- Client API (Tenant Durable Object, proxied by the Worker at /api/clients/:id/d/*) ----------

export const ConversationState = z.enum(['assistant', 'human', 'closed'])
export type ConversationState = z.infer<typeof ConversationState>

export const Author = z.enum(['contact', 'assistant', 'owner', 'system'])
export type Author = z.infer<typeof Author>

export interface ConversationSummary {
  id: string
  contactId: string
  channel: ChannelKind
  contactName: string | null
  /** E.164 digits; null on Messenger and for WhatsApp contacts known only by LID. */
  contactPhone: string | null
  state: ConversationState
  needsYou: boolean
  needsYouReason: string | null
  replyCount: number
  /** When the owner marked the lead as booked; no follow-ups after that. */
  bookedAt: number | null
  lastMessageAt: number
  lastMessagePreview: string
}

export interface MessageView {
  id: string
  author: Author
  text: string
  at: number
  /** Channel message ID (WhatsApp ID or Messenger mid); matches AuditEntry.sentMessageId for assistant messages. */
  waMessageId: string | null
  /** Owner's rating of an assistant message, if any. */
  rating: 'good' | 'bad' | null
}

export interface ConversationDetail extends ConversationSummary {
  messages: MessageView[]
  optedOut: boolean
}

export interface TodaySummary {
  newInquiries: number
  assistantReplies: number
  needsYou: number
  medianFirstReplyMs: number | null
  /** Channels the Tenant serves (set by control on connect). */
  channels: TenantChannel[]
  assistantLive: boolean
  configVersion: number
}

export interface UsageView {
  month: string
  /** Leads this month (counted conversations, all channels). */
  conversations: number
  leadCap: number
  inputTokens: number
  outputTokens: number
  spendEur: number
  budgetEur: number
}

/** WebSocket frames on GET /api/clients/:id/d/live (JSON text, one event per frame). */
export type LiveEvent =
  | { type: 'message'; conversationId: string; message: MessageView }
  | { type: 'conversation'; conversation: ConversationSummary }
  | { type: 'channels'; channels: TenantChannel[] }
  /** One channel's state changed (token_invalid, payment_issue, disconnected, connected again); the D1 view. */
  | { type: 'channel'; channel: ChannelStatus }
  | { type: 'notice'; notice: TenantNotice }

// ---------- Dashboard view of a client (Worker -> web) ----------

export type BillingStatus = 'none' | 'trialing' | 'active' | 'past_due' | 'unpaid' | 'canceled' | 'incomplete'

export interface ClientView {
  id: string
  businessName: string
  website: string
  /** Empty when the client uses no WhatsApp alerts. */
  ownerPhone: string
  status: ClientStatus
  billingStatus: BillingStatus
  trialEndsAt: number | null
  checklist: {
    profile: boolean
    calibration: boolean
    jobs: boolean
    payment: boolean
    whatsapp: boolean
    messenger: boolean
    /** At least one channel connected (required to go live). */
    channel: boolean
    live: boolean
  }
  /** One entry per channel kind (CHANNEL_KINDS order), `not_connected` when none. */
  channels: ChannelStatus[]
  /**
   * Owner test code (8 digits), created on the first channel connect. The owner sends "LOBBY <code>" once from their
   * own WhatsApp (a second phone with Coexistence), Messenger or Instagram account to mark it as theirs for testing.
   */
  ownerCode: string | null
  /** @deprecated Use `channels`. The connected Facebook Page, null when none. `ownerCode` is no longer issued. */
  messenger: { pageId: string; pageName: string; status: 'connected' | 'token_invalid'; ownerCode?: string; connectedAt: number } | null
  provisioning: { step: string; startedAt: number | null; error: string | null } | null
  config: { pushedVersion: number; appliedVersion: number }
  /** The client's runtime (Tenant Durable Object); null before provisioning or after erase. */
  tenant: TenantStatus | null
}

export interface MeResponse {
  user: { id: string; email: string }
  client: ClientView | null
  /** Normalised website the owner started with on the landing page (`POST /auth/login`); null once a client exists. */
  pendingWebsite: string | null
  /** The signed-in user is the operator (email equals OPERATOR_EMAIL): the operator page is available. */
  isOperator: boolean
  /** Channel connect is configured on this deployment: `messenger` = Facebook Login (Messenger, Instagram, comments), `whatsapp` = Embedded Signup. */
  features: { messenger: boolean; whatsapp: boolean }
}

/** GET /api/channels/meta/pages: Pages the owner granted, after the Facebook login, with the linked Instagram account. Tokens stay in the Worker. */
export interface MetaPageOption {
  id: string
  name: string
  instagram?: { id: string; username: string }
}
export const ConnectMetaBody = z.object({
  /** Login state from the callback redirect (`?pick=`); optional, the owner's latest login is used otherwise. */
  pick: z.string().min(16).max(128).optional(),
  pageId: z.string().min(1).max(64),
  messenger: z.boolean(),
  instagram: z.boolean(),
  comments: z.boolean().default(true),
})
export type ConnectMetaBody = z.infer<typeof ConnectMetaBody>

/** POST /api/channels/whatsapp/connect: Embedded Signup result (code from FB.login, IDs from the session info message). */
export const ConnectWhatsAppBody = z.object({
  code: z.string().min(1).max(2000),
  wabaId: z.string().regex(/^\d{1,32}$/),
  phoneNumberId: z.string().regex(/^\d{1,32}$/),
})
export type ConnectWhatsAppBody = z.infer<typeof ConnectWhatsAppBody>

/** Embedded Signup `extras.setup` (pre-filled data), built from the Lobby business profile. */
export interface EmbeddedSignupSetup {
  business: {
    name: string
    email?: string
    website?: string
    address?: { country: string }
    /** UTC offset, e.g. `UTC+01:00`. */
    timezone: string
  }
  /** `category`: a WhatsApp business profile `vertical` (OTHER, HEALTH, BEAUTY, ...). `description` at most 512 characters. */
  phone: { displayName: string; category: string; description: string }
}

/** GET /api/channels/whatsapp/config. FB.login: `{ config_id: configId, response_type: 'code', override_default_response_type: true, extras: { ...extras, setup: prefill } }`. */
export interface WhatsAppSignupConfig {
  appId: string
  configId: string
  graphVersion: string
  prefill: EmbeddedSignupSetup
  extras: { featureType: 'whatsapp_business_app_onboarding'; sessionInfoVersion: '3' }
  /** Embedded Signup version. Selected by the Login for Business configuration (`configId`), not an FB.login parameter. */
  version: 'v4'
}

// ---------- Operator page (GET /api/operator/overview) ----------
// Statistics only: clients appear by ID, never with a business name, email, phone or message content.

export interface OperatorClientRow {
  id: string
  status: ClientStatus
  billingStatus: BillingStatus
  createdAt: number
  /** Last statistics snapshot the client's Tenant wrote to D1 (`tenant_stores.stats`); null before the first one. */
  tenant: { statsAt: number; channels: TenantChannel[] } | null
  /** This month in the Tenant (assistant replies, classifier, judge, suggestions). */
  month: { conversations: number; testConversations: number; inputTokens: number; outputTokens: number; spendEur: number; budgetEur: number }
  /** All time in the control plane (profile, calibration and rule drafts). */
  onboarding: { inputTokens: number; outputTokens: number; costEur: number }
  needsYou: number
  /** Tenant data in Cloudflare; null before the first provisioning. */
  store: { lastBackupAt: number | null; bytes: number | null; erasedAt: number | null; expiredAt: number | null } | null
}

export interface OperatorOverview {
  generatedAt: number
  clients: { total: number; byStatus: Record<string, number>; byBilling: Record<string, number> }
  /** Paying subscriptions times the monthly price. */
  mrrEur: number
  month: { conversations: number; testConversations: number; inputTokens: number; outputTokens: number; spendEur: number; needsYou: number }
  onboarding: { inputTokens: number; outputTokens: number; costEur: number }
  rows: OperatorClientRow[]
}

// ---------- Guardrails (Tenant, every turn) ----------
// Four gates around the main agent; all model calls go through Strands agents with structured output.

/** Gate 1: input classifier (Strands agent, structured output). */
export const INPUT_CATEGORIES = ['in_scope', 'out_of_scope', 'injection', 'emergency', 'complaint', 'human_request', 'abuse'] as const
export const InputClassification = z.object({
  category: z.enum(INPUT_CATEGORIES),
  reason: z.string().max(200),
})
export type InputClassification = z.infer<typeof InputClassification>

/** Gate 2: the main agent's structured reply. Code checks factsUsed, prices and links before anything is sent. */
export const REPLY_ACTIONS = ['answer', 'ask', 'book', 'close', 'handover'] as const
export const ReplyDraft = z.object({
  reply: z.string().min(1).max(1200),
  action: z.enum(REPLY_ACTIONS),
  /** Profile field names the reply relies on. Every fact in the reply must come from these. */
  factsUsed: z.array(z.enum(PROFILE_FIELDS as [ProfileField, ...ProfileField[]])).max(12),
  /** Rule IDs from the config the reply follows. */
  rulesUsed: z.array(z.string().max(40)).max(20),
  /** Set when action is handover. */
  handoverReason: z.string().max(200).optional(),
})
export type ReplyDraft = z.infer<typeof ReplyDraft>

/** Gate 3: the output judge (separate Strands agent, structured output). */
export const JUDGE_VIOLATIONS = [
  'ungrounded_fact',
  'off_objective',
  'professional_advice',
  'config_leak',
  'unlisted_price',
  'unlisted_link',
  'wrong_language',
  'tone',
  'pushy',
  'missing_disclosure',
] as const
export const JudgeViolation = z.enum(JUDGE_VIOLATIONS)
export type JudgeViolation = z.infer<typeof JudgeViolation>
export const JudgeVerdict = z.object({
  pass: z.boolean(),
  violations: z.array(JudgeViolation).max(10),
  note: z.string().max(300),
})
export type JudgeVerdict = z.infer<typeof JudgeVerdict>

export const TURN_OUTCOMES = ['sent', 'sent_fallback', 'handed_over', 'refused', 'blocked_by_rule', 'error'] as const
export type TurnOutcome = (typeof TURN_OUTCOMES)[number]

/** Deterministic checks run in code on every ReplyDraft (gate 2). */
export const CODE_CHECKS = ['facts_confirmed', 'prices_listed', 'links_listed', 'rules_exist', 'length', 'disclosure'] as const
export type CodeCheckName = (typeof CODE_CHECKS)[number]

/** Gate 4: one audit entry per turn, stored in the Tenant only. GET /api/conversations/:id/audit. */
export interface AuditEntry {
  id: string
  conversationId: string
  at: number
  configVersion: number
  /** Versions of the safety block and each agent prompt in this build. */
  promptVersions: Record<string, string>
  modelId: string
  ruleDecision: string
  input: InputClassification | null
  draft: Omit<ReplyDraft, 'reply'> | null
  codeChecks: { name: CodeCheckName; pass: boolean; detail?: string }[]
  judge: JudgeVerdict | null
  attempts: number
  toolCalls: { name: string; ok: boolean }[]
  outcome: TurnOutcome
  outcomeReason: string
  usage: { inputTokens: number; outputTokens: number; costEur: number }
  /** WhatsApp message ID of what was sent, if anything; the text itself is in the transcript. */
  sentMessageId: string | null
}

// ---------- Learning from takeovers and ratings (SPEC.md 4.4) ----------

export interface RuleSuggestion {
  id: string
  text: string
  /** Local preference record IDs (takeover pairs, ratings) behind it. */
  evidence: string[]
  /** De-identified excerpts of the evidence, shown to the owner. */
  examples: { contactMessage: string; assistantDraft: string; ownerReply: string }[]
  status: 'proposed' | 'accepted' | 'rejected'
  createdAt: number
}

/**
 * Owner alert from the Tenant: emailed with a fixed text per kind and a dashboard link, and stored as a dashboard notice.
 * No contact data.
 */
export const OWNER_NOTICE_KINDS = [
  'needs_you',
  'daily_cap',
  'monthly_cap',
  'budget_80',
  'budget_100',
  'messenger_token_invalid',
  'channel_token_invalid',
  'channel_payment_issue',
  'channel_disconnected',
] as const
export const OwnerNotice = z.object({
  kind: z.enum(OWNER_NOTICE_KINDS),
  /** Fixed reason text from the Tenant (NEEDS_YOU_REASONS) or a channel name; never model or contact text. */
  reason: z.string().max(100).optional(),
  conversationId: z.string().max(64).optional(),
})
export type OwnerNotice = z.infer<typeof OwnerNotice>

/** The owner accepted a suggestion in the Tenant. Names and numbers already removed in code (deidentify). */
export const AcceptedSuggestion = z.object({
  suggestionId: z.string().max(40),
  rule: z.string().min(1).max(300),
  preferences: z.array(PreferenceRecord).min(1).max(20),
  draftedBy: z.object({ agent: z.string().max(60), promptVersion: z.string().max(40), modelId: z.string().max(80) }),
})
export type AcceptedSuggestion = z.infer<typeof AcceptedSuggestion>

export const MessageRating = z.object({ rating: z.enum(['good', 'bad']), note: z.string().max(500).optional() })
export type MessageRating = z.infer<typeof MessageRating>

// ---------- Worker-side agent calls (GET /api/clients/:id/model-calls) ----------

export interface ModelCallView {
  id: string
  agent: string
  promptVersion: string
  modelId: string
  usage: { inputTokens: number; outputTokens: number }
  outcome: 'ok' | 'invalid' | 'error'
  error: string | null
  at: number
}
